import { IRestaurant } from '@rbi-ctg/store';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { ServiceMode } from 'state/service-mode/types';

import { IServiceModeHeading } from './types';

export const SERVICE_MODES = [
  ServiceMode.DELIVERY,
  ServiceMode.DRIVE_THRU,
  ServiceMode.EAT_IN,
  ServiceMode.MOBILE_ORDER_DRIVE_THRU,
  ServiceMode.TAKEOUT,
];

export const PICKUP_SERVICE_MODES = [
  ServiceMode.DRIVE_THRU,
  ServiceMode.EAT_IN,
  ServiceMode.MOBILE_ORDER_DRIVE_THRU,
  ServiceMode.TAKEOUT,
];

export const DELIVERY_SERVICE_MODES = [ServiceMode.DELIVERY];

export const isDelivery = (serviceMode: ServiceMode | null): serviceMode is ServiceMode.DELIVERY =>
  serviceMode === ServiceMode.DELIVERY;

export const isDriveThru = (
  serviceMode: ServiceMode | null
): serviceMode is ServiceMode.DRIVE_THRU => serviceMode === ServiceMode.DRIVE_THRU;

export const isMobileOrderDriveThru = (
  serviceMode: ServiceMode | null
): serviceMode is ServiceMode.MOBILE_ORDER_DRIVE_THRU =>
  serviceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU;

export const isPickup = (serviceMode: ServiceMode | null): serviceMode is ServiceMode.DELIVERY =>
  serviceMode ? PICKUP_SERVICE_MODES.includes(serviceMode) : false;

export const getLocalizedServiceMode = (serviceMode: ServiceMode | null): string => {
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      return t('driveThru');
    case ServiceMode.EAT_IN:
      return t('eatIn');
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      return t('mobileOrderDriveThru');
    case ServiceMode.TAKEOUT:
      return t('takeOut');
    case ServiceMode.DELIVERY:
      return t('delivery');
    default:
      return '';
  }
};

export const getHeadingForServiceMode = (
  isFastLaneEnabled: boolean,
  isMobileOrderForDriveThruLaneEnabled: boolean,
  serviceMode?: ServiceMode | null
): IServiceModeHeading | null => {
  let heading: IServiceModeHeading | null;

  switch (serviceMode) {
    case ServiceMode.DELIVERY:
      heading = {
        icon: getServiceModeIcon(serviceMode),
        heading: t('delivery'),
      };
      break;
    case ServiceMode.TAKEOUT: {
      const headingKey = isFastLaneEnabled ? 'fastLaneDriveThru' : 'takeOut';
      heading = {
        icon: getServiceModeIcon(serviceMode),
        heading: t(headingKey),
      };
      break;
    }
    case ServiceMode.DRIVE_THRU: {
      const headingKey = isMobileOrderForDriveThruLaneEnabled ? 'mobileOrderLane' : 'driveThru';
      heading = {
        icon: getServiceModeIcon(serviceMode),
        heading: t(headingKey),
      };
      break;
    }
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      heading = {
        icon: getServiceModeIcon(serviceMode),
        heading: t('mobileOrderDriveThru'),
      };
      break;
    case ServiceMode.EAT_IN:
      heading = {
        icon: getServiceModeIcon(serviceMode),
        heading: t('dineIn'),
      };
      break;
    default:
      heading = null;
  }

  return heading;
};

// The Top Service Mode Bar should only differentiate between pickup and delivery.
export const getTsmBarHeadingForServiceMode = (
  serviceMode?: ServiceMode | null
): IServiceModeHeading | null => {
  if (!serviceMode) {
    return null;
  }

  const isPickupServiceMode = isPickup(serviceMode);
  if (isPickupServiceMode) {
    return {
      icon: 'store',
      heading: t('pickUp'),
    };
  }

  return {
    icon: 'delivery',
    heading: t('delivery'),
  };
};

export const getServiceModeIcon = (serviceMode?: ServiceMode | null): string => {
  const iconMap: { [key in ServiceMode]?: string } = {
    [ServiceMode.DELIVERY]: 'delivery',
    [ServiceMode.TAKEOUT]: 'store',
    [ServiceMode.DRIVE_THRU]: 'driveThru',
    [ServiceMode.MOBILE_ORDER_DRIVE_THRU]: 'mobileOrderDriveThru',
    [ServiceMode.EAT_IN]: 'restaurant',
  };

  return serviceMode ? iconMap[serviceMode] || '' : '';
};

/*
  If MOBILE_ORDER_DRIVE_THRU is enabled and available, use it. If not, use DRIVE_THRU.
*/
export const getPreferredDriveThruServiceMode = (
  isMobileOrderDriveThruEnabledForStore: boolean,
  isServiceModeAvailable: (store: IRestaurant, serviceMode: ServiceMode) => boolean,
  store: IRestaurant
): ServiceMode => {
  if (
    isMobileOrderDriveThruEnabledForStore &&
    isServiceModeAvailable(store, ServiceMode.MOBILE_ORDER_DRIVE_THRU)
  ) {
    return ServiceMode.MOBILE_ORDER_DRIVE_THRU;
  }

  return ServiceMode.DRIVE_THRU;
};

export const getServiceModeDescriptionTextId = (
  serviceMode: ServiceMode
): TranslationKey | null => {
  switch (serviceMode) {
    case ServiceMode.TAKEOUT:
      return 'serviceModeDetailsPickupDescriptionV2';
    case ServiceMode.DRIVE_THRU:
      return 'serviceModeDetailsDriveThruDescriptionV2';
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      return 'serviceModeDetailsMobileOrderDriveThruDescriptionV2';
    case ServiceMode.EAT_IN:
      return 'serviceModeDetailsDineInDescriptionV2';
    default:
      return null;
  }
};
